// src/app/pages/ReferralPage/index.tsx
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Copy, Check, Coins, Share2, Gift, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import type { ReferralSummaryDto, ReferredUserDto } from "./types";
import { ReferralController } from "./controller/ReferralController";
import { ReferralService } from "@/app/network/service/ReferralService.ts";
import { Button } from "@/app/core/ui/neitral.ui/button";
import { ListSkeleton } from "@/app/core/ui/skeletons/ListSkeleton";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

export function ReferralPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const controller = useMemo(() => new ReferralController(), []);
  const referralService = useMemo(() => ReferralService.getInstance(), []);

  const [copied, setCopied] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const [summary, setSummary] = useState<ReferralSummaryDto | null>(null);
  const [referredUsers, setReferredUsers] = useState<ReferredUserDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [summaryRes, usersRes] = await Promise.all([
          referralService.getSummary(),
          referralService.getReferredUsers(),
        ]);
        setSummary(summaryRes.data);
        setReferredUsers(usersRes.data);
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "Не удалось загрузить данные";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [referralService]);

  const handleCopyLink = () => {
    if (!summary?.referralLink) return;
    controller.notifySuccess();
    navigator.clipboard.writeText(summary.referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatJoinDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="-ml-2 rounded-lg p-2 hover:bg-secondary transition-colors">
              <ArrowLeft className="h-5 w-5 text-foreground" />
            </button>
            <h1 className="text-xl font-bold text-foreground">{t('inviteFriends')}</h1>
            <div className="flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5">
              <Coins className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-semibold text-foreground">
                {isLoading ? "—" : (summary?.referralCount ?? 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600">
            <Gift className="h-8 w-8 text-white" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-foreground">{t('inviteAndEarn')}</h2>
          {summary && (
            <p className="text-sm text-muted-foreground">
              {t('friendDiscount').replace('{discount}', String(summary.firstPurchaseDiscountPercent))}
            </p>
          )}
        </motion.div>

        {error && (
          <div className="mb-6 rounded-2xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Share2 className="h-5 w-5 text-violet-600" />
            <h3 className="font-semibold text-foreground">{t('yourReferralLink')}</h3>
          </div>
          <div className="mb-4 rounded-xl border border-border bg-secondary px-4 py-3 text-sm text-foreground break-all">
            {isLoading ? (
              <span className="inline-block h-4 w-48 animate-pulse rounded bg-muted" />
            ) : (
              summary?.referralLink ?? "—"
            )}
          </div>
          <Button
            variant={copied ? "secondary" : "primary"}
            size="lg"
            onClick={handleCopyLink}
            disabled={isLoading || !summary?.referralLink}
            className={`w-full ${copied ? "bg-green-500 text-white hover:bg-green-600" : "bg-violet-600 hover:bg-violet-700"}`}
          >
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            {copied ? t('copied') : t('copyLink')}
          </Button>
        </div>

        <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">
            {t('recentInvites')}
            {!isLoading && referredUsers.length > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">({referredUsers.length})</span>
            )}
          </h3>

          {isLoading ? (
            <ListSkeleton rows={3} avatar />
          ) : referredUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('noInvites')}</p>
          ) : (
            referredUsers.map((user) => (
              <div key={user.referralId} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500 text-white text-sm font-bold">
                    {String(user.telegramId).slice(-2)}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">User #{user.telegramId}</span>
                    <p className="text-xs text-muted-foreground">{formatJoinDate(user.joinedAt)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <button
            onClick={() => { setIsRulesOpen(!isRulesOpen); controller.selection(); }}
            className="flex w-full items-center justify-between p-5"
          >
            <h3 className="font-semibold text-foreground">{t('howItWorks')}</h3>
            <ChevronDown className={`h-5 w-5 text-foreground transition-transform ${isRulesOpen ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {isRulesOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="px-5 pb-5 text-sm text-muted-foreground space-y-2"
              >
                <p>{t('step1')}</p>
                <p>{t('step2')}</p>
                {summary && (
                  <p>{t('step3').replace('{discount}', String(summary.firstPurchaseDiscountPercent))}</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}